'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import CartItem from './CartItem';
import PriceBreakdown from './PriceBreakdown';
import PromoCodeInput from './PromoCodeInput';
import SuggestedProducts from './SuggestedProducts';
import EmptyCart from './EmptyCart';

interface CartItemData {
  id: string;
  name: string;
  image: string;
  alt: string;
  weight: string;
  pricePerKg: number;
  quantity: number;
  totalPrice: number;
  inStock: boolean;
  priceChange?: {
    type: 'increase' | 'decrease';
    percentage: number;
  };
}

interface SuggestedProduct {
  id: string;
  name: string;
  image: string;
  alt: string;
  pricePerKg: number;
  weight: string;
  inStock: boolean;
}

const ShoppingCartInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [cartItems, setCartItems] = useState<CartItemData[]>([
  {
    id: '1',
    name: 'Fresh Atlantic Salmon',
    image: "https://images.unsplash.com/photo-1673767415033-b99c536cc370",
    alt: 'Fresh pink Atlantic salmon fillet on white surface with ice crystals',
    weight: '1kg',
    pricePerKg: 850,
    quantity: 2,
    totalPrice: 1700,
    inStock: true,
    priceChange: { type: 'decrease', percentage: 2.5 }
  },
  {
    id: '2',
    name: 'Premium Tuna Steak',
    image: "https://images.unsplash.com/photo-1469550213474-5d8433aa956f",
    alt: 'Red tuna steak cuts displayed on dark slate surface with garnish',
    weight: '500g',
    pricePerKg: 1200,
    quantity: 1,
    totalPrice: 600,
    inStock: true,
    priceChange: { type: 'increase', percentage: 3.2 }
  },
  {
    id: '3',
    name: 'Fresh Tiger Prawns',
    image: "https://images.unsplash.com/photo-1633244092689-37963a4d9ac3",
    alt: 'Large tiger prawns arranged on ice with lemon slices and herbs',
    weight: '750g',
    pricePerKg: 680,
    quantity: 1,
    totalPrice: 510,
    inStock: true
  }]
  );

  const [appliedPromoCode, setAppliedPromoCode] = useState('');

  const suggestedProducts: SuggestedProduct[] = [
  {
    id: '4',
    name: 'Fresh Mackerel',
    image: "https://images.unsplash.com/photo-1711649801345-59b4308d03b9",
    alt: 'Whole mackerel fish with silver scales on white background',
    pricePerKg: 320,
    weight: '500g',
    inStock: true
  },
  {
    id: '5',
    name: 'Sea Bass Fillet',
    image: "https://images.unsplash.com/photo-1617918379589-3d4cec850352",
    alt: 'White sea bass fillet portions on wooden cutting board',
    pricePerKg: 750,
    weight: '1kg',
    inStock: true
  },
  {
    id: '6',
    name: 'Fresh Squid',
    image: "https://images.unsplash.com/photo-1623910270365-9b45727235c4",
    alt: 'Cleaned squid rings and tentacles on ice display',
    pricePerKg: 450,
    weight: '500g',
    inStock: false
  },
  {
    id: '7',
    name: 'King Crab Legs',
    image: "https://images.unsplash.com/photo-1517253692881-2c00609b4ee2",
    alt: 'Red king crab legs arranged on dark plate with butter sauce',
    pricePerKg: 2200,
    weight: '1kg',
    inStock: true
  }];


  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  const calculateDeliveryCharges = () => {
    const subtotal = calculateSubtotal();
    return subtotal >= 500 ? 0 : 40;
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.05;
  };

  const calculateDiscount = () => {
    if (!appliedPromoCode) return 0;
    const subtotal = calculateSubtotal();
    if (appliedPromoCode === 'FRESH10') return subtotal * 0.1;
    if (appliedPromoCode === 'SAVE50') return 50;
    if (appliedPromoCode === 'FISHBITE20') return subtotal * 0.2;
    return 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateDeliveryCharges() + calculateTax() - calculateDiscount();
  };

  const handleQuantityChange = (id: string, quantity: number) => {
    setCartItems((items) =>
    items.map((item) => {
      if (item.id === id) {
        const weightInKg = parseFloat(item.weight.replace('kg', '').replace('g', '')) / (item.weight.includes('g') && !item.weight.includes('kg') ? 1000 : 1);
        const totalPrice = item.pricePerKg * weightInKg * quantity;
        return { ...item, quantity, totalPrice };
      }
      return item;
    })
    );
  };

  const handleWeightChange = (id: string, weight: string) => {
    setCartItems((items) =>
    items.map((item) => {
      if (item.id === id) {
        const weightInKg = parseFloat(weight.replace('kg', '').replace('g', '')) / (weight.includes('g') && !weight.includes('kg') ? 1000 : 1);
        const totalPrice = item.pricePerKg * weightInKg * item.quantity;
        return { ...item, weight, totalPrice };
      }
      return item;
    })
    );
  };

  const handleRemoveItem = (id: string) => {
    setCartItems((items) => items.filter((item) => item.id !== id));
  };

  const handleSaveForLater = (id: string) => {
    console.log('Save for later:', id);
  };

  const handleApplyPromoCode = (code: string) => {
    setAppliedPromoCode(code);
  };

  const handleAddToCart = (productId: string) => {
    console.log('Add to cart:', productId);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-[140px] pb-16">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-48 mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                {[1, 2, 3].map((i) =>
                <div key={i} className="h-40 bg-muted rounded-md"></div>
                )}
              </div>
              <div className="h-96 bg-muted rounded-md"></div>
            </div>
          </div>
        </div>
      </div>);

  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-background pt-[140px] pb-16">
        <div className="container mx-auto px-4 lg:px-6">
          <EmptyCart />
        </div>
      </div>);

  }

  const priceBreakdown = {
    subtotal: calculateSubtotal(),
    deliveryCharges: calculateDeliveryCharges(),
    tax: calculateTax(),
    discount: calculateDiscount(),
    total: calculateTotal()
  };

  return (
    <div className="min-h-screen bg-background pt-[140px] pb-16">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground mb-2">
              Shopping Cart
            </h1>
            <p className="text-muted-foreground">
              {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your cart
            </p>
          </div>
          <Link
            href="/product-listing"
            className="flex items-center gap-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-md transition-smooth">

            <Icon name="ArrowLeftIcon" size={18} />
            <span className="font-medium text-sm">Continue Shopping</span>
          </Link>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) =>
            <CartItem
              key={item.id}
              item={item}
              onQuantityChange={handleQuantityChange}
              onWeightChange={handleWeightChange}
              onRemove={handleRemoveItem}
              onSaveForLater={handleSaveForLater} />

            )}

            {/* Promo Code */}
            <PromoCodeInput onApply={handleApplyPromoCode} />
          </div>

          {/* Price Breakdown */}
          <div>
            <PriceBreakdown
              breakdown={priceBreakdown}
              estimatedDelivery="Tomorrow, 10:00 AM - 12:00 PM" />

          </div>
        </div>

        {/* Suggested Products */}
        <SuggestedProducts products={suggestedProducts} onAddToCart={handleAddToCart} />

        {/* Trust Badges */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 p-4 bg-card border border-border rounded-md">
            <Icon name="ShieldCheckIcon" size={24} className="text-primary flex-shrink-0" />
            <div>
              <div className="font-medium text-sm text-foreground">100% Secure Payment</div>
              <div className="text-xs text-muted-foreground">Your data is protected</div>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-card border border-border rounded-md">
            <Icon name="TruckIcon" size={24} className="text-primary flex-shrink-0" />
            <div>
              <div className="font-medium text-sm text-foreground">Fast Delivery</div>
              <div className="text-xs text-muted-foreground">Same day delivery available</div>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-card border border-border rounded-md">
            <Icon name="ArrowPathIcon" size={24} className="text-primary flex-shrink-0" />
            <div>
              <div className="font-medium text-sm text-foreground">Easy Returns</div>
              <div className="text-xs text-muted-foreground">Hassle-free return policy</div>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default ShoppingCartInteractive;